/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    image = "image",
    textFieldValue = "textFieldValue",
    multilineTextFieldValue = "multilineTextFieldValue",
    textColor = "textColor",
}

interface Props extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.textFieldValue]: string;
        [fields.multilineTextFieldValue]: string;
        [fields.textColor]: string;
    };
}

const INTCustomSubPageBanner: React.FC<Props> = ({ fields }) => {
    const containerStyles = {
        css: css`
            background-image: url(${fields.image});
            min-height: 497px;
            background-size: cover;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 259px;
                        `,
                        css`
                            min-height: 259px;
                        `,
                        css`
                            min-height: 259px;
                        `,
                        css`
                            min-height: 296px;
                        `,
                        css`
                            min-height: 296px;
                        `,
                    ],
                    "min",
                )}
        `,
    };
    return (
        <StyledWrapper {...containerStyles}>
            <StyledWrapper
                css={css`
                    max-width: 1140px;
                    margin: 0 auto;
                    width: 100%;
                    display: flex;
                    // justify-content: flex-end;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    max-width: 100% !important;
                                `,
                                css`
                                    max-width: 100% !important;
                                `,
                                css`
                                    max-width: 720px !important;
                                `,
                                css`
                                    max-width: 960px !important;
                                `,
                                css`
                                    max-width: 1140px !important;
                                `,
                            ],
                            "min",
                        )}
                `}
            >
                <StyledWrapper
                    css={css`
                        width: 60.5%;
                        margin-bottom: 63px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        width: 100%;
                                        padding: 30px;
                                        margin-bottom: 0px;
                                    `,
                                    css`
                                        width: 100%;
                                        padding: 30px;
                                        margin-bottom: 0px;
                                    `,
                                    css`
                                        width: 100%;
                                        padding: 0px;
                                        margin-bottom: 63px;
                                    `,
                                    css`
                                        width: 70.5%;
                                        padding: 0px;
                                        margin-bottom: 63px;
                                    `,
                                ],
                                "minMax",
                            )}
                    `}
                >
                    <Typography
                        as="h3"
                        css={css`
                            color: ${fields.textColor};
                            font-size: 18px;
                            font-weight: bold;
                            letter-spacing: 1.08px;
                            line-height: 25px;
                            padding-bottom: 10px;
                        `}
                    >
                        {fields.textFieldValue}
                    </Typography>
                    {fields.multilineTextFieldValue && (
                        <Typography
                            as="p"
                            css={css`
                                color: ${fields.textColor};
                                font-size: 44px;
                                font-weight: 300;
                                letter-spacing: 0;
                                line-height: 53px;
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(
                                        theme,
                                        [
                                            css`
                                                font-size: 36px;
                                                font-weight: 300;
                                                letter-spacing: 0;
                                                line-height: 43px;
                                            `,
                                            css`
                                                font-size: 36px;
                                                font-weight: 300;
                                                letter-spacing: 0;
                                                line-height: 43px;
                                            `,
                                            css`
                                                font-size: 36px;
                                                font-weight: 300;
                                                letter-spacing: 0;
                                                line-height: 43px;
                                            `,
                                            css`
                                                font-size: 44px;
                                                font-weight: 300;
                                                letter-spacing: 0;
                                                line-height: 53px;
                                            `,
                                            css`
                                                font-size: 44px;
                                                font-weight: 300;
                                                letter-spacing: 0;
                                                line-height: 53px;
                                            `,
                                        ],
                                        "min",
                                    )}
                            `}
                        >
                            {fields.multilineTextFieldValue}
                        </Typography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.textFieldValue,
            displayName: "Text",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            tooltip: "text tip",
            placeholder: "text placeholder",
            isRequired: true,
        },
        {
            name: fields.multilineTextFieldValue,
            displayName: "MultilineText",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            tooltip: "multi line tip",
            placeholder: "multi line placeholder",
            isRequired: true,
        },
        {
            name: fields.image,
            displayName: "Photo",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 1,
        },
        {
            name: fields.textColor,
            editorTemplate: "DropDownField",
            defaultValue: "Light",
            fieldType: "General",
            options: [
                { displayName: "Light", value: "#ffffff" },
                { displayName: "Dark", value: "#333333" },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: INTCustomSubPageBanner,
    definition,
};

export default widgetModule;
